// Frontend TypeScript types for the ESA Panel — aligned to AgOps API responses
// via the ESA Agent proxy. See design specs: 6400_DESIGN_SPECS-esa-panel.md

// ─── Conversation ─────────────────────────────────────────────────────────────

export interface Conversation {
  id: string;
  workscope_ukey: string | null;
  title: string | null;
  conversation_type: string | null;
  business_objective: string | null;
  conversation_metadata: Record<string, unknown>;
  tags: string[];
  status: string;
  created_by: string | null;
  metad_created_at: string;
  metad_mod_at: string;
}

export interface ConversationListResponse {
  items: Conversation[];
  total: number;
}

// ─── Messages ─────────────────────────────────────────────────────────────────

export interface ConversationMessage {
  id: string;
  conversation_id: string;
  parent_message_id: string | null;
  initiator: 'user' | 'system';
  message_type: 'text' | 'json' | 'mix';
  message: Record<string, unknown>;
  has_attachments: boolean;
  attachment_type: string | null;
  stored_file_ukey: string | null;
  metad_created_at: string;
}

export interface ConversationMessageListResponse {
  items: ConversationMessage[];
  total: number;
}

// ─── Message Attachments ──────────────────────────────────────────────────────

/** Lightweight reference to a stored file attached to a conversation message. */
export interface MessageAttachmentRef {
  stored_file_ukey: string;
  file_name?: string;
  content_type?: string;
}

// ─── Converse ─────────────────────────────────────────────────────────────────

export interface ConverseRequest {
  message: string;
  session_id?: string;
  tenant_ukey?: string;
  initiator_id?: string;
  context?: Record<string, unknown>;
}

/** Structured directive telling the UI to render an embedded chart/view. */
export interface UiDirectiveEmbedView {
  type: 'embed_view';
  embed_view_url: string;
  title: string;
  chart_type?: string;
}

/** Union of all possible ui_directive shapes (extensible for future types). */
export type UiDirective = UiDirectiveEmbedView;

/**
 * A single open-ask choice after normalization.
 * DESO ships either bare strings (`*_confirm` kinds) or `{id, label}` objects
 * (`semi_clarify`, `scope_compile`, `brain_slot`, `dossier_confirm`).
 */
export interface OpenCommitmentOption {
  /** Stable option id when DESO supplies one (e.g. an attribute ukey). */
  id?: string;
  /** Row text, and what is sent back as the reply. */
  label: string;
  /** One-line consequence of picking this option; rendered under the label. */
  hint?: string;
  /** Letter shown in the badge. Falls back to A/B/C by position. */
  key?: string;
}

/**
 * DESO Open Commitment (feat-112 / ADR-0037), normalized for rendering.
 * Rendered as a lettered option list the user can click or answer by typing.
 */
export interface OpenCommitment {
  kind: string;
  question: string;
  options?: OpenCommitmentOption[];
  frame_id?: string;
  /**
   * Position in a multi-question clarify flow, zero-based. Drives the
   * "Question n of m" kicker, which is hidden unless `total` exceeds one.
   */
  index?: number;
  total?: number;
  /** Overrides the default "Pick one, or just type A–C" footer line. */
  free_hint?: string;
  /** When false, the footer stops inviting a free-text answer. */
  allow_free?: boolean;
}

export interface ConverseResponse {
  message: string;
  session_id: string;
  turn_number: number;
  continue_conversation: boolean;
  /** Structured UI directive returned alongside the message (e.g. embed_view). */
  ui_directives?: UiDirective;
  /** PRD-050: structured response blocks — additive alongside `message` (DR-11). */
  blocks?: Block[];
  metadata: {
    conversation_id?: string;
    route?: string;
    reasoning?: string;
    last_message_id?: string;
    error_code?: string;
    agops_degraded?: boolean;
    /** ADS artifact key for agent-generated visualization. Track C+ (BD-7). */
    visualizationSpecRef?: string;
    /** DESO open ask for chip UI — raw wire; normalize via parseOpenCommitment. */
    open_commitment?: unknown;
    [key: string]: unknown;
  };
}

// ─── Structured Response Blocks (PRD-050) ─────────────────────────────────────
// TypeScript mirror of GAF's Block union (gaxl-agent-foundation, tag v0.2.34,
// src/gaxl_agent_foundation/domain/models/blocks.py). Any shape change on the
// GAF side requires re-vendoring the fixtures under __fixtures__/blocks/ and
// updating these interfaces in lockstep (DR-14, enforced by blockContract.test.ts).

export type BlockType =
  | 'bullet_list'
  | 'numbered_list'
  | 'table'
  | 'paragraph'
  | 'code'
  | 'metric'
  | 'comparison'
  | 'timeline'
  | 'approval_card'
  | 'chart_embed'
  | 'follow_up_chips';

export interface BulletListBlock {
  type: 'bullet_list';
  items: string[];
}

export interface NumberedListBlock {
  type: 'numbered_list';
  items: string[];
}

export interface TableBlock {
  type: 'table';
  headers: string[];
  rows: string[][];
}

export interface ParagraphBlock {
  type: 'paragraph';
  text: string;
}

export interface CodeBlock {
  type: 'code';
  language?: string;
  content: string;
}

export interface MetricBlock {
  type: 'metric';
  label: string;
  value: string;
  detail?: string;
}

export interface ComparisonOption {
  label: string;
  detail?: string;
}

export interface ComparisonBlock {
  type: 'comparison';
  options: ComparisonOption[];
}

export interface TimelineStep {
  name: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  detail?: string | null;
}

export interface TimelineBlock {
  type: 'timeline';
  steps: TimelineStep[];
}

export interface ApprovalCardBlock {
  type: 'approval_card';
  question: string;
  options: string[];
  hil_request_id?: string;
}

export interface ChartEmbedBlock {
  type: 'chart_embed';
  embed_view_url: string;
  title?: string;
  chart_type?: string;
}

export interface FollowUpChipsBlock {
  type: 'follow_up_chips';
  suggestions: string[];
}

export type Block =
  | BulletListBlock
  | NumberedListBlock
  | TableBlock
  | ParagraphBlock
  | CodeBlock
  | MetricBlock
  | ComparisonBlock
  | TimelineBlock
  | ApprovalCardBlock
  | ChartEmbedBlock
  | FollowUpChipsBlock;

const KNOWN_BLOCK_TYPES: ReadonlySet<string> = new Set<BlockType>([
  'bullet_list',
  'numbered_list',
  'table',
  'paragraph',
  'code',
  'metric',
  'comparison',
  'timeline',
  'approval_card',
  'chart_embed',
  'follow_up_chips',
]);

/** Type guard: true for any of the 11 known block type discriminants. */
export function isKnownBlockType(value: unknown): value is BlockType {
  return typeof value === 'string' && KNOWN_BLOCK_TYPES.has(value);
}

// ─── DESO Structured Content ──────────────────────────────────────────────────

export type DesoResponseType =
  | 'direct_answer'
  | 'continue_conversation'
  | 'plan_presentation'
  | 'task_dispatched'
  | 'hil_escalation';

export interface DesoStructuredContent {
  response_type: DesoResponseType;
  message: string;
  questions?: string[];
  plan?: Record<string, unknown>;
  task_id?: string;
  follow_ups?: string[];
  /** ADS artifact key for agent-generated visualization. Track C+ (BD-7). */
  visualizationSpecRef?: string;
}

// ─── Relay ────────────────────────────────────────────────────────────────────

export type RelayResponseType =
  | 'task_progress'
  | 'task_completed'
  | 'task_failed'
  | 'hil_escalation';

// ─── HITL ─────────────────────────────────────────────────────────────────────

// Raw AgOps /services/ops/hil-requests item (snake_case, as returned by the API)
export interface AgOpsHilOrigin {
  task_id: string | null;
  step_ref: string | null;
  agent_ukey: string | null;
  execution_id: string | null;
  workscope_ukey: string | null;
}

/** Structured HIL choice (GAF HITLOption). Wire `value` is what agents resolve on. */
export interface AgOpsHilOption {
  value: string;
  label?: string;
  is_default?: boolean;
  requires_input?: boolean;
  metadata?: Record<string, unknown>;
}

export type AgOpsHilOptionInput = string | AgOpsHilOption;

export interface AgOpsHilContext {
  conversation_id: string | null;
  solution_agent_key: string | null;
  origin: AgOpsHilOrigin | null;
  urgency: string | null;
  question: string | null;
  options?: AgOpsHilOptionInput[];
  [key: string]: unknown;
}

export interface AgOpsHilMetadata {
  options?: AgOpsHilOptionInput[];
  [key: string]: unknown;
}

export interface AgOpsHilItem {
  id: string;
  agent_run_id: string;
  resolution_agent_run_id: string | null;
  resolved_by_agent_ukey: string | null;
  agent_ukey: string;
  solution_agent_ukey: string | null;
  conversation_id: string | null;
  step_ref: string | null;
  question: string | null;
  request_type: string | null;
  status: string;
  priority: string;
  human_answer: Record<string, unknown> | null;
  reviewer_id: string | null;
  reviewed_at: string | null;
  metad_tenant_ukey: string;
  metad_created_at: string;
  metad_mod_at: string;
  context: AgOpsHilContext | null;
  hil_metadata: AgOpsHilMetadata | null;
  hil_group_id: string | null;
}

export interface AgOpsHilListResponse {
  items: AgOpsHilItem[];
  total: number;
  offset: number;
  limit: number;
}

// ─── AgOps HIL search response (hil-requests/search) ─────────────────────────

/** The `data` payload inside each search result item. context and hil_metadata
 *  are Python-serialized strings from AgOps and must be parsed with helpers. */
export interface AgOpsHilSearchDataItem {
  id: string;
  agent_run_id: string;
  resolution_agent_run_id: string | null;
  resolved_by_agent_ukey: string | null;
  hil_group_id: string | null;
  conversation_id: string | null;
  agent_ukey: string;
  step_ref: string | null;
  question: string | null;
  request_type: string | null;
  context: string | null;
  priority: string;
  status: string;
  human_answer: Record<string, unknown> | null;
  reviewer_id: string | null;
  reviewed_at: string | null;
  hil_metadata: string | null;
  metad_tenant_ukey: string;
  metad_created_at: string;
  metad_mod_at: string;
}

export interface AgOpsHilSearchResultItem {
  concept_type: string;
  concept_ukey: string;
  score: number | null;
  data: AgOpsHilSearchDataItem;
}

export interface AgOpsHilSearchCursor {
  after_value: string;
  after_ukey: string;
  has_more: boolean;
}

export interface AgOpsHilSearchResponse {
  items: AgOpsHilSearchResultItem[];
  cursor: AgOpsHilSearchCursor;
  total: number;
}


// ─── ESA Inbox — Host-local types (DR-4, DR-5, DR-9) ────────────────────────

import type { CapabilityId } from '@growthaxl/web-foundation';

/**
 * Extends the shared CapabilityId with the host-local 'resolving' value.
 * 'resolving' is a shell-operational tab, not a canvas-authored capability.
 * Not added to the foundation CapabilityId to avoid polluting the shared contract.
 */
export type ExtendedCapabilityId = CapabilityId | 'resolving';

/** Spec for the dynamic resolving tab injected into CapabilityTabsLayout. */
export interface ResolvingTabSpec {
  title: string;
  hilId: string;
  moduleId: string;
  solutionId: string;
  /** Markdown string rendered in the dynamic canvas tab panel. Populated by the
   *  host when starting a resolve session; will be fetched from the backend in
   *  future iterations. */
  markdownContent?: string;
  /** True while ResolvingView is lazily fetching full HIL details for this item.
   *  CapabilityTabsLayout shows a loading spinner instead of the incomplete
   *  generated-markdown fallback during this window. */
  isLoadingDetail?: boolean;
}

/** Active resolution session tracking multiple HIL items. */
export interface ResolvingSession {
  items: InboxItem[];
  currentIndex: number;
}

// ─── ESA Inbox — Data model ───────────────────────────────────────────────────

export interface InboxAttention {
  title: string;
  summary: string;
  count: number;
  objective: string;
  solution: string;
  run: string;
}

export interface InboxItem {
  id: string;
  type: 'hitl' | 'approval';
  status: 'open' | 'resolved';
  name: string;
  raisedAt: string;
  priority: string;
  urgency: 'high' | 'medium' | 'low';
  attention: InboxAttention;
  linkedItemTitle: string | null;
  /** Human-readable chip labels shown and sent from the resolve UI. */
  suggestions: string[];
  moduleId: string;
  solutionId: string;
  agentUkey: string;
  workscopeUkey: string;
  conversationId: string | null;
  uiPresentation: string | null;
  /** Structured related-evidence payload (e.g. profiling attribute check table). */
  artifact?: InboxArtifact;
  messages?: InboxMessage[];
}

export interface InboxMessage {
  role: 'agent' | 'user';
  text: string;
  artifact?: InboxArtifact;
}

export interface InboxArtifact {
  id: string;
  title: string;
  icon: string;
  type: 'table' | 'chart' | 'text';
  columns?: string[];
  rows?: string[][];
}

export interface InboxNotification {
  id: string;
  urgency: 'high' | 'normal';
  kind: 'success' | 'alert' | 'info';
  title: string;
  body: string;
  solution: string;
  objective?: string;
  time: string;
  read: boolean;
  link?: { tab: string; label: string };
}

export interface InboxSummary {
  pending: number;
  approved: number;
  rejected: number;
}

export interface InboxListCursor {
  after_ukey: string | null;
  has_more: boolean;
}

export interface InboxListResponse {
  items: InboxItem[];
  total: number;
  offset: number;
  limit: number;
  degraded: boolean;
  summary: InboxSummary;
  cursor?: InboxListCursor;
}
